import apolloClient from "@/apollo/apollo";
import api from "@/utils/api";
import gql from "graphql-tag";

const state = {
    patientRequests: [],
};

const getters = {
    getAllPatientRequests: (state) => state.patientRequests,
};

const mutations = {
    setAllPatientRequests(state, patientRequests) {
        state.patientRequests = patientRequests.slice();
    },
};

function graphQLErrorMessage(error, fallback) {
    if (error && error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
        return error.graphQLErrors[0].message;
    }
    return fallback;
}

const actions = {
    async fetchAllPatientRequests({ commit }, { status, requestTypeId, priority, search, searchField, dateFrom, dateTo, page, size }) {
        try {
            const response = await apolloClient.query({
                query: gql`
          query findAllPatientRequests($status: RequestStatusEnum, $requestTypeId: ID, $priority: RequestPriorityEnum, $search: String, $searchField: RequestSearchField, $dateFrom: String, $dateTo: String, $page: Int, $size: Int) {
            findAllPatientRequests(status: $status, requestTypeId: $requestTypeId, priority: $priority, search: $search, searchField: $searchField, dateFrom: $dateFrom, dateTo: $dateTo, page: $page, size: $size) {
              content {
                id
                title
                description
                status
                priority
                staffNotes
                processedDate
                creationDate
                dueDate
                lastMessageAt
                lastMessageByStaff
                rating
                reviewComment
                reviewedAt
                requestType {
                  id
                  name
                }
                patient {
                  id
                  contact {
                    fullName
                  }
                }
                user {
                  id
                  contact {
                    fullName
                  }
                }
                processedBy {
                  id
                  contact {
                    fullName
                  }
                }
              }
              totalPages
              totalElements
              number
              size
            }
          }
        `,
                variables: {
                    status: status || null,
                    requestTypeId: requestTypeId || null,
                    priority: priority || null,
                    search: search || null,
                    searchField: searchField || null,
                    dateFrom: dateFrom || null,
                    dateTo: dateTo || null,
                    page: page || 0,
                    size: size || 15,
                },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
                fetchPolicy: "network-only",
            });
            return response.data.findAllPatientRequests;
        } catch (error) {
            console.error("Failed to fetch patient requests:", error);
            throw new Error(graphQLErrorMessage(error, "Failed to fetch patient requests"));
        }
    },

    async updatePatientRequestStatus({ dispatch }, { id, updateInput }) {
        try {
            const response = await apolloClient.mutate({
                mutation: gql`
          mutation updatePatientRequestStatus($id: ID!, $updateInput: UpdatePatientRequestStatusInput!) {
            updatePatientRequestStatus(id: $id, updateInput: $updateInput) {
              id
              status
              priority
              staffNotes
            }
          }
        `,
                variables: { id, updateInput },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
            });
            return response.data.updatePatientRequestStatus;
        } catch (error) {
            console.error("Failed to update patient request status:", error);
            throw new Error(graphQLErrorMessage(error, "Failed to update patient request"));
        }
    },

    async replyToPatientRequest(_, { id, body, internal }) {
        try {
            const response = await apolloClient.mutate({
                mutation: gql`
          mutation replyToPatientRequest($id: ID!, $replyInput: ReplyToPatientRequestInput!) {
            replyToPatientRequest(id: $id, replyInput: $replyInput) {
              id
              body
              internal
              creationDate
              sender {
                id
                email
                contact {
                  fullName
                }
              }
            }
          }
        `,
                variables: { id, replyInput: { body, internal: !!internal } },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
            });
            return response.data.replyToPatientRequest;
        } catch (error) {
            console.error("Failed to reply to patient request:", error);
            throw new Error(graphQLErrorMessage(error, "Failed to send reply"));
        }
    },

    async fetchPatientRequestMessages(_, { id }) {
        try {
            const response = await apolloClient.query({
                query: gql`
          query patientRequestMessages($id: ID!) {
            patientRequestMessages(id: $id) {
              id
              body
              internal
              creationDate
              sender {
                id
                email
                contact {
                  fullName
                }
              }
            }
          }
        `,
                variables: { id },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
                fetchPolicy: "network-only",
            });
            return response.data.patientRequestMessages || [];
        } catch (error) {
            console.error("Failed to fetch request messages:", error);
            throw new Error(graphQLErrorMessage(error, "Failed to fetch replies"));
        }
    },

    async fetchPatientRequestById(_, { id }) {
        try {
            const response = await apolloClient.query({
                query: gql`
          query findOnePatientRequestById($id: ID!) {
            findOnePatientRequestById(id: $id) {
              id
              title
              description
              status
              priority
              staffNotes
              processedDate
              creationDate
              dueDate
              lastMessageAt
              rating
              reviewComment
              reviewedAt
              requestType {
                id
                name
              }
              patient {
                id
                contact {
                  fullName
                }
              }
              user {
                id
                contact {
                  fullName
                }
              }
              processedBy {
                id
                contact {
                  fullName
                }
              }
            }
          }
        `,
                variables: { id },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
                fetchPolicy: "network-only",
            });
            return response.data.findOnePatientRequestById;
        } catch (error) {
            console.error("Failed to fetch patient request:", error);
            throw new Error(graphQLErrorMessage(error, "Failed to fetch request"));
        }
    },

        async fetchRequestAttachments(_, { id }) {
        try {
            const response = await apolloClient.query({
                query: gql`
          query patientRequestAttachments($id: ID!) {
            patientRequestAttachments(id: $id) {
              id
              messageId
              fileName
              mime
              sizeBytes
              creationDate
            }
          }
        `,
                variables: { id },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
                fetchPolicy: "network-only",
            });
            return response.data.patientRequestAttachments || [];
        } catch (error) {
            console.error("Failed to fetch request attachments:", error);
            return [];
        }
    },

    async uploadRequestAttachment(_, { requestId, messageId, file }) {
        try {
            const form = new FormData();
            form.append("file", file);
            if (messageId) form.append("messageId", messageId);
            const response = await api.post(`/api/patient-requests/${requestId}/attachments`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response.data;
        } catch (error) {
            console.error("Failed to upload attachment:", error);
            const serverMsg = error?.response?.data?.message || error?.response?.data;
            throw new Error(typeof serverMsg === "string" ? serverMsg : "Failed to upload attachment");
        }
    },

    async downloadRequestAttachment(_, { requestId, attachment }) {
        const response = await api.get(
            `/api/patient-requests/${requestId}/attachments/${attachment.id}`,
            { responseType: "blob" }
        );
        const blob = new Blob([response.data], {
            type: response.headers["content-type"] || attachment.mime || "application/octet-stream",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", attachment.fileName || "attachment");
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 60 * 1000);
    },

    async fetchRequestStats() {
        try {
            const response = await apolloClient.query({
                query: gql`
          query patientRequestStats {
            patientRequestStats {
              pending
              inProgress
              completedToday
              cancelled
            }
          }
        `,
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
                fetchPolicy: "network-only",
            });
            return response.data.patientRequestStats || null;
        } catch (error) {
            console.error("Failed to fetch request stats:", error);
            return null;
        }
    },

    async fetchPatientRequestHistory(_, { id }) {        try {
            const response = await apolloClient.query({
                query: gql`
          query patientRequestHistory($id: ID!) {
            patientRequestHistory(id: $id) {
              id
              fromStatus
              toStatus
              note
              creationDate
            }
          }
        `,
                variables: { id },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
                fetchPolicy: "network-only",
            });
            return response.data.patientRequestHistory || [];
        } catch (error) {
            console.error("Failed to fetch request history:", error);
            return [];
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
